import { Observable, of } from 'rxjs'
import {
  concatMap,
  debounceTime,
  filter,
  ignoreElements,
  map,
  mergeAll,
  mergeMap,
  pluck,
  switchMap,
  tap,
} from 'rxjs/operators'
import { get } from 'lodash-es'
import deepmerge from 'deepmerge'
import { Epic } from 'redux-observable'
import jwtDecode from 'jwt-decode'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { IS_TEST_MODE } from 'constants/general'
import { EpicDependencies, RootState } from 'src/state/types'
import { EMPTY } from 'rxjs'
import { catchError, exhaustMap } from 'rxjs/operators'
import { agencyApiEvents } from '../agency'
import { AgencyEvent } from '../agency/state'
import { events, UserEvent } from './state'
import { ENDPOINTS } from './constants'
import { deleteTokens, getMisc, isTokenExpired, setMisc, setTokensFromResponse, TDecodedAuthToken } from 'utils/auth'
import { multiUserOperators } from '../../multiUser/state'
import { uiEvents, UiEvent } from '../../ui/state'
import { agenciesWithOutstandingAccount } from './constants'
import { restfulErrorEvent } from 'utils/restful'
import {
  TAdminAgencyMember,
  TAdminEmailMatch,
  TAdminUserLookup,
  TAgencyUser,
  TAuthTokens,
  TBulkAddOutcome,
  TDecodedTokenEvent,
  TOnboardingProgress,
  TTeamMember,
  TUserDetails,
  TUserMisc,
} from './types'

type UserEpic = Epic<UserEvent, UserEvent, RootState, EpicDependencies>

export const prefetchUsersOnAgencyMembersFetched: Epic<
  UserEvent | AgencyEvent,
  UserEvent | AgencyEvent,
  RootState,
  EpicDependencies
> = action$ =>
  action$.pipe(
    filter(isOfType(agencyApiEvents.users_success.type)),
    mergeMap(({ payload }) => payload.map(p => events.userDetails_request({ id: p.userId }))),
    tag('user/epic/prefetchUsersOnAgencyMembersFetched'),
  )

/**
 * @todo should check JWT age based on api funnel incoming events (with a timer)
 */
// const token = getJWTToken()
// if (token && isTokenExpired(token) && !store.getState().user.isRefreshingToken) {
//   store.dispatch(userActions.apiRefreshToken())

export const setJwtTokens: Epic<UserEvent, never, RootState, EpicDependencies> = action$ =>
  action$.pipe(
    filter(
      isOfType([
        events.signup_success.type,
        events.passwordReset_success.type,
        events.agencySwitched.type,
        // The switcher reloads the page straight after this; App resumes from storage, so
        // without persisting here the reload restores the previous agency's token.
        events.switchAgency_success.type,
        events.login_success.type,
        events.agencyLogin_success.type,
        events.refreshToken_success.type,
      ]),
    ),
    pluck('payload'),
    tap(setTokensFromResponse),
    tag('user/epic/setJwtTokens'),
    ignoreElements(),
  )

/** The pair of events `decodeUserAuthTokens` can emit. */
type TDecodeEvent = ReturnType<typeof events.userAuthTokenDecoded> | ReturnType<typeof events.userAuthTokenExpired>

/**
 * Turn a token pair into either `userAuthTokenDecoded` or `userAuthTokenExpired`,
 * keeping the response it came from on `source` so the caller can pass it on to
 * its own `*_success` event.
 */
const decodeUserAuthTokens = (payload$: Observable<TAuthTokens>): Observable<TDecodeEvent> =>
  payload$.pipe(
    map(payload => {
      const { authToken, refreshToken } = payload
      const decoded = jwtDecode<TDecodedAuthToken>(authToken)
      if (!IS_TEST_MODE && isTokenExpired(decoded)) {
        return events.userAuthTokenExpired({
          source: payload,
          refreshToken,
        })
      }
      return events.userAuthTokenDecoded({
        source: payload,
        ...decoded,
        // aud: JSON.parse(get(decoded, 'aud.0', '[]')),
        // sub: JSON.parse(get(decoded, 'sub', '[]'))
      })
    }),
  )

/** The `source` a decode event carries — i.e. the token pair the epic started from. */
const decodedSource = (event: { payload: TDecodedTokenEvent }): TAuthTokens => event.payload.source

export const refreshToken: UserEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.refreshToken_request.type)),
    mergeMap(action =>
      put(ENDPOINTS.REFRESH_TOKEN, state$.value.api.user.refreshToken ?? '', action.payload, {}).pipe(
        pluck('response'),
        decodeUserAuthTokens,
        concatMap(event => [event, events.refreshToken_success(decodedSource(event))]),
        catchRestError(action),
      ),
    ),
    tag('user/epics/refreshToken'),
  )

export const loginUser: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.login_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.LOGIN, null as unknown as string, action.payload, {}).pipe(
        pluck('response'),
        decodeUserAuthTokens,
        concatMap((event): UserEvent[] => {
          const agencyId = event.payload.value?.agencyMembership?.agencyId
          if (agencyId !== undefined && agenciesWithOutstandingAccount.includes(agencyId)) {
            return [
              event,
              restfulErrorEvent(
                {
                  response: {
                    errors: [
                      {
                        key: 'email',
                        message: 'You have an outstanding balance on your account. Please contact accounts@reos.co.za',
                      },
                    ],
                  },
                },
                {
                  initAction: action,
                  reduceByPayload: true,
                },
              ) as unknown as UserEvent,
            ]
          }
          return [event, events.login_success(decodedSource(event))]
        }),
        catchRestError(action),
      ),
    ),
    tag('user/epics/loginUser'),
  )

export const agencyLogin: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.agencyLogin_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.AGENCY_LOGIN, state$, action.payload, {}).pipe(
        pluck('response'),
        decodeUserAuthTokens,
        concatMap(event => [event, events.agencyLogin_success(decodedSource(event))]),
        catchRestError(action),
      ),
    ),
    tag('user/epics/agencyLogin'),
  )

export const registerUser: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.signup_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.REGISTER, null as unknown as string, action.payload, {}).pipe(
        pluck('response'),
        decodeUserAuthTokens,
        concatMap(event => [event, events.signup_success(decodedSource(event))]),
        catchRestError(action),
      ),
    ),
    tag('user/epics/registerUser'),
  )

export const registerUserFromInvite: UserEpic = (action$, state$, { post, catchRestError }) => {
  return action$.pipe(
    filter(isOfType(events.signupFromInvite_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.REGISTER_FROM_INVITE, null as unknown as string, action.payload, {}).pipe(
        pluck('response'),
        decodeUserAuthTokens,
        concatMap(event => [event, events.signup_success(decodedSource(event))]),
        catchRestError(action),
      ),
    ),
    tag('user/epics/registerUserFromInvite'),
  )
}

export const resumeUser: UserEpic = action$ =>
  action$.pipe(
    filter(isOfType(events.userResumed.type)),
    pluck('payload'),
    decodeUserAuthTokens,
    concatMap(event => [
      of(event),
      of(event).pipe(
        filter(isOfType(events.userAuthTokenDecoded.type)),
        map(decoded => events.login_success(decodedSource(decoded))),
      ),
    ]),
    mergeAll(),
    tag('user/epics/loginUser'),
  )

export const logoutUser: Epic<UserEvent | UiEvent, UserEvent | UiEvent, RootState, EpicDependencies> = action$ =>
  action$.pipe(
    filter(isOfType(events.logout_request.type)),
    pluck('payload'),
    tap(deleteTokens),
    mergeMap(payload => {
      const timeOut = get(payload, 'timeOut', false)
      const emitted: Array<UserEvent | UiEvent> = []
      if (timeOut) {
        emitted.push(events.logout_success({ timeOut }))
      }
      emitted.push(events.logout_success(null))
      emitted.push(uiEvents.redirect('/login'))
      return emitted
    }),
    tag('user/epics/logoutUser'),
  )

// Fire and forget: a missed beat only makes the user look idle for a minute, so failures are
// dropped rather than surfaced. exhaustMap skips a beat while the previous one is in flight.
export const heartbeat: Epic<UserEvent, never, RootState, EpicDependencies> = (action$, state$, { post }) =>
  action$.pipe(
    filter(isOfType(events.heartbeat_request.type)),
    exhaustMap(() =>
      post(ENDPOINTS.HEARTBEAT, state$, {}, {}).pipe(
        ignoreElements(),
        catchError(() => EMPTY),
      ),
    ),
    tag('user/epics/heartbeat'),
  )

export const forgotPassword: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.resetPasswordLink_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.FORGOT_PASSWORD, state$, action.payload, {}).pipe(
        pluck('response'),
        map((response: { userId: string }) => events.resetPasswordLink_success(response)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/forgotPassword'),
  )

export const resetPassword: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.passwordReset_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.RESET_PASSWORD, action.payload.token, action.payload.payload, {}).pipe(
        pluck('response'),
        decodeUserAuthTokens,
        concatMap(event => [event, events.passwordReset_success(decodedSource(event))]),
        catchRestError(action),
      ),
    ),
    tag('user/epics/resetPassword'),
  )

export const getCurrentUserDetails: UserEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.currentUserDetails_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.GET_CURRENT_USER_DETAILS, state$, {}).pipe(
        pluck('response'),
        map((response: TUserDetails) => events.currentUserDetails_success(response)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/getCurrentUserDetails'),
  )

export const getUserDetails: UserEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.userDetails_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.GET_USER_DETAILS, state$, action.payload).pipe(
        pluck('response'),
        map((response: TUserDetails) => events.userDetails_success({ ...response, ...action.payload })),
        catchRestError(action),
      ),
    ),
    tag('user/epics/getUserDetails'),
  )

export const saveUserDetails: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.saveDetails_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.GET_CURRENT_USER_DETAILS, state$, action.payload, {}).pipe(
        pluck('response'),
        map((response: TUserDetails) => events.saveDetails_success(response)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/saveUserDetails'),
  )

export const validateOtp: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.validateOtp_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.VALIDATE_OTP, state$, action.payload, {}).pipe(
        pluck('response'),
        map((response: TAuthTokens) => events.validateOtp_success(response)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/validateOtp'),
  )

export const verifyEmail: UserEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.verifyEmail_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.VERIFY_EMAIL, state$, action.payload).pipe(
        pluck('response'),
        decodeUserAuthTokens,
        concatMap(event => [event, events.verifyEmail_success(), events.login_success(decodedSource(event))]),
        catchRestError(action),
      ),
    ),
    tag('user/epics/verifyEmail'),
  )

export const resendEmailVerification: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.resendEmailVerification_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.RESEND_VERIFICATION, state$, {}, {}).pipe(
        pluck('response'),
        map((response: null) => events.resendEmailVerification_success(response)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/resendEmailVerification'),
  )

export const createAgency: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.createAgency_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.CREATE_AGENCY, state$, action.payload, {}).pipe(
        pluck('response'),
        decodeUserAuthTokens,
        concatMap(event => [
          event,
          events.createAgency_success(decodedSource(event)),
          events.agencySwitched(decodedSource(event)),
        ]),
        catchRestError(action),
      ),
    ),
    tag('user/epics/createAgency'),
  )

export const updateMisc: UserEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.updateMisc_request.type)),
    mergeMap(action => {
      const stored = getMisc()
      const misc: TUserMisc = stored ? deepmerge(stored, action.payload) : action.payload
      return put(ENDPOINTS.UPDATE_MISC, state$, misc, {}).pipe(
        pluck('response'),
        map((response: TUserMisc) => events.updateMisc_success(response)),
        tap(({ payload }) => setMisc(payload)), // Update storage
        catchRestError(action),
      )
    }),
    tag('user/epics/updateMisc'),
  )

export const getOnboardingProgress: UserEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.onboardingProgress_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.ONBOARDING_PROGRESS, state$, {}).pipe(
        pluck('response'),
        map((response: TOnboardingProgress) => events.onboardingProgress_success(response)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/getOnboardingProgress'),
  )

export const fetchTeam: UserEpic = (action$, state$, { get, catchRestError }) => {
  return action$.pipe(
    filter(isOfType(events.team_request.type)),
    multiUserOperators.filterOnboardedAndOwner(state$),
    mergeMap(action =>
      get(ENDPOINTS.TEAM, state$, {}).pipe(
        pluck('response'),
        map((response: TTeamMember[]) => events.team_success(response)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/fetchTeam'),
  )
}

export const revokeAccess: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.revokeAccess_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.REVOKE_ACCESS, state$, action.payload, {}).pipe(
        pluck('response'),
        map((response: unknown) => events.revokeAccess_success(response, action.payload)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/revokeAccess'),
  )

export const changePermissions: UserEpic = (action$, state$, { put, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.changePermissions_request.type)),
    mergeMap(action =>
      put(ENDPOINTS.PERMISSIONS, state$, action.payload.body, action.payload.params).pipe(
        pluck('response'),
        map(() => events.changePermissions_success(action.payload)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/changePermissions'),
  )

export const enableUser: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.enableUser_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.ENABLE_USER, state$, action.payload, {}).pipe(
        pluck('response'),
        map(() => events.enableUser_success({ email: action.payload.email })),
        catchRestError(action),
      ),
    ),
    tag('user/epics/enableUser'),
  )

export const disableUser: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.disableUser_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.DISABLE_USER, state$, action.payload, {}).pipe(
        pluck('response'),
        map(() => events.disableUser_success({ email: action.payload.email })),
        catchRestError(action),
      ),
    ),
    tag('user/epics/disableUser'),
  )

export const getAgencies: UserEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.getAgencies_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.GET_AGENCIES, state$, {}).pipe(
        pluck('response'),
        map((res: TAgencyUser[]) => events.getAgencies_success(res)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/getAgencies'),
  )

export const switchAgency: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.switchAgency_request.type)),
    mergeMap(action =>
      post(ENDPOINTS.SWITCH_AGENCY, state$, action.payload, {}).pipe(
        pluck('response'),
        decodeUserAuthTokens,
        // `decodeUserAuthTokens` keeps the response it decoded on `source`, so the token
        // pair can be handed to switchAgency_success alongside the decode event itself.
        concatMap((event): UserEvent[] => [event, events.switchAgency_success(event.payload.source)]),
        catchRestError(action),
      ),
    ),
    tag('user/epics/switchAgency'),
  )

export const adminFetchAgencyMembers: UserEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.adminAgencyMembers_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.ADMIN_AGENCY_MEMBERS, state$, { agencyId: action.payload.agencyId }).pipe(
        pluck('response'),
        map((res: TAdminAgencyMember[]) => events.adminAgencyMembers_success(res, action.payload)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/adminFetchAgencyMembers'),
  )

export const adminLookupUser: UserEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.adminLookupUser_request.type)),
    // Only the latest email typed matters.
    switchMap(action =>
      get(ENDPOINTS.ADMIN_LOOKUP_USER, state$, { email: action.payload.email.trim() }).pipe(
        pluck('response'),
        map((res: TAdminUserLookup) => events.adminLookupUser_success(res)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/adminLookupUser'),
  )

/**
 * Below this the backend returns nothing, so the request is skipped. Not exported: root.ts
 * registers every export of this module as an epic.
 */
const ADMIN_EMAIL_SEARCH_MIN = 3

export const adminSearchEmails: UserEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.adminSearchEmails_request.type)),
    // Fired per keystroke; a term without a full domain can scan the whole table server-side.
    debounceTime(300),
    switchMap(action => {
      const q = action.payload.q.trim()
      if (q.length < ADMIN_EMAIL_SEARCH_MIN) return of(events.adminSearchEmails_success([]))
      return get(ENDPOINTS.ADMIN_SEARCH_EMAILS, state$, { q }).pipe(
        pluck('response'),
        map((res: TAdminEmailMatch[]) => events.adminSearchEmails_success(res)),
        catchRestError(action),
      )
    }),
    tag('user/epics/adminSearchEmails'),
  )

export const adminGrantAccess: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.adminGrantAccess_request.type)),
    mergeMap(action =>
      post(
        ENDPOINTS.BULK_ADD_AGENCY_MEMBER,
        state$,
        { userId: action.payload.userId, agencies: action.payload.agencies },
        {},
      ).pipe(
        pluck('response'),
        map((res: { results: TBulkAddOutcome[] }) => events.adminGrantAccess_success(res.results, action.payload)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/adminGrantAccess'),
  )

export const adminRemoveMembership: UserEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.adminRemoveMembership_request.type)),
    mergeMap(action =>
      post(
        ENDPOINTS.ADMIN_REMOVE_MEMBERSHIP,
        state$,
        { userId: action.payload.userId, agencyId: action.payload.agencyId },
        {},
      ).pipe(
        map(() => events.adminRemoveMembership_success(null, action.payload)),
        catchRestError(action),
      ),
    ),
    tag('user/epics/adminRemoveMembership'),
  )

// Re-read the user and every agency touched by a grant, so the page shows what the backend now holds.
export const adminRefreshAfterGrant: UserEpic = action$ =>
  action$.pipe(
    filter(isOfType(events.adminGrantAccess_success.type)),
    mergeMap(({ meta }) => [
      events.adminLookupUser_request({ email: meta.email }),
      ...meta.agencies.map(a => events.adminAgencyMembers_request({ agencyId: a.agencyId })),
    ]),
    tag('user/epics/adminRefreshAfterGrant'),
  )
